import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useModal } from '@ampol/design-system';
import ModalContentWrapper from 'components/modal-content-wrapper/modal-content-wrapper';
import { selectAddUserModalState } from 'features/users/selectors/addUserModalSelectors';
import { useEmailValidation } from 'features/users/hooks/useEmailValidation';
import { useAddUserFlow } from 'features/users/hooks/useAddUserFlow';
import { useAddUserModalLifecycle } from 'features/users/hooks/useAddUserModalLifecycle';
import { useAddUserModalConfig } from 'features/users/hooks/useAddUserModalConfig.jsx';
import Env from 'Env';

export const steps = {
  EmailLookup: 1,
  AdminAssignAccounts: 2,
  CustomerPortalAssignAccounts: 3,
  AddUserConfirmation: 4,
};

/**
 * Modal for add new user flow
 * @returns JSX Element
 */
const AddUserModal = ({ onClose }) => {
  const isAdminPortal = Env.isAdminPortal();

  const {
    assignedGrandfatherClientNumber,
    loggedInUser,
    assignedAccountNumber,
    assignRolesList,
    userEmailLookupLoaded,
    userEmailLookupLoading,
    userEmailLookupError,
    userEmailLookup,
    searchUserExists: userExists,
    searchUserExistsLoaded: userExistsLoaded,
    searchUserExistsLoading: userExistsLoading,
    searchUserExistsError: userExistsError,
    adminSearchUserExists: adminUserExists,
    adminSearchUserExistsLoaded: adminUserExistsLoaded,
    adminSearchUserExistsLoading: adminUserExistsLoading,
    adminSearchUserExistsError: adminUserExistsError,
    createUserLoading,
    createUserLoaded,
    primaryActionButton,
    secondaryActionButton,
    canReadWriteUsers: canReadWrite,
  } = useSelector(selectAddUserModalState);

  const [adminAssignAccountsSubmitButtonDisabled, setAdminAssignAccountsSubmitButtonDisabled] = useState(true);

  const [email, setEmail] = useState('');

  const emailLookupFormId = 'email-lookup-form';
  const addUserDetailsFormId = 'add-user-details-form';
  const adminAddUserAssignAccountsFormId = 'admin-add-user-assign-accounts-form';

  // Handle email validation logic (replaces complex useEffect + state management)
  const { addUserDetailsNextButtonDisabled, formId, setFormId } = useEmailValidation({
    userEmailLookupLoaded,
    userEmailLookupLoading,
    userEmailLookupError,
    userEmailLookup,
    userExists,
    userExistsLoaded,
    userExistsLoading,
    userExistsError,
    adminUserExists,
    adminUserExistsLoaded,
    adminUserExistsLoading,
    adminUserExistsError,
    addUserDetailsFormId,
  });

  // Handle add user workflow and step management
  const {
    stepId,
    setStepId,
    userDetails,
    roleId,
    handleAddUserDetailsSubmit,
    handleCustomerAddUser,
    handleAdminAssignAccountsSubmit,
    getRoleIdByName,
    closeModal,
    resetUserDetails,
  } = useAddUserFlow({
    steps,
    email,
    assignRolesList,
    assignedGrandfatherClientNumber,
    loggedInUser,
    assignedAccountNumber,
    setFormId,
    addUserDetailsFormId,
    adminAddUserAssignAccountsFormId,
    onClose,
  });

  // Handle modal lifecycle (initialization, cleanup, data fetching)
  useAddUserModalLifecycle({
    assignRolesList,
    assignedGrandfatherClientNumber,
    createUserLoaded,
    steps,
    setStepId,
  });

  // Configure modal steps and action buttons
  const { currentStep, getActionButtons } = useAddUserModalConfig({
    steps,
    stepId,
    emailLookupFormId,
    addUserDetailsFormId,
    adminAddUserAssignAccountsFormId,
    email,
    setEmail,
    resetUserDetails,
    setFormId,
    userDetails,
    handleAddUserDetailsSubmit,
    handleAdminAssignAccountsSubmit,
    setAdminAssignAccountsSubmitButtonDisabled,
    handleCustomerAddUser,
    canReadWrite,
    setStepId,
    roleId,
    userEmailLookupLoading,
    addUserDetailsNextButtonDisabled,
    createUserLoading,
    createUserLoaded,
    adminAssignAccountsSubmitButtonDisabled,
    primaryActionButton,
    secondaryActionButton,
    closeModal,
  });

  const modalProps = {
    id: 'add-new-user-modal',
    formId: formId,
    title: currentStep.title,
    subtitle: currentStep.subtitle,
    content: (
      <ModalContentWrapper
        currentStep={currentStep}
        variant={
          currentStep.stepId === steps.AdminAssignAccounts || currentStep.stepId === steps.CustomerPortalAssignAccounts
            ? 'triple'
            : 'double'
        }
      />
    ),
    variant:
      currentStep.stepId === steps.AdminAssignAccounts || currentStep.stepId === steps.CustomerPortalAssignAccounts
        ? 'triple'
        : 'double',
    onClose: closeModal,
    actions: getActionButtons(),
    color: 'blue',
  };

  const addNewUserModal = useModal(modalProps);

  useEffect(() => {
    addNewUserModal.open();

    return () => {
      addNewUserModal.close();
    };
  }, []);

  return addNewUserModal.content;
};

export default AddUserModal;
